/**
 * MembersPage - View & manage trip members
 * Route: /trips/:id/members
 * Design: tripways.pen node UWQWT (desktop) / PAQFj (mobile)
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronsLeft, Compass, LogOut, UserPlus } from 'lucide-react';
import { apiGet } from '../services/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useTripMembers } from '../hooks/useTripMembers';
import { MemberApiError } from '../services/memberService';
import DesktopSidebar from '../components/common/DesktopSidebar';
import ConfirmDialog from '../components/common/ConfirmDialog';
import MemberRow from '../components/members/MemberRow';
import type { TripDetailResponse } from '../types/trip';
import type { MemberRole } from '../types/member';

export default function MembersPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();

  const [tripName, setTripName] = useState('');
  const [myRole, setMyRole] = useState<MemberRole>('EDITOR');
  const { members, loading, changeMemberRole, removeMember, leaveTrip } = useTripMembers(id);

  const [confirmTarget, setConfirmTarget] = useState<{ userId: number; nickname: string } | null>(null);
  const [confirmLeave, setConfirmLeave] = useState(false);

  useEffect(() => {
    if (!id) return;
    apiGet<TripDetailResponse>(`/trip/${id}`).then((response) => {
      if (response.exitCode === 1 && response.data) {
        setTripName(response.data.name);
        setMyRole(response.data.myRole);
      }
    });
  }, [id]);

  if (!id || !user) return null;

  const handleChangeRole = async (targetUserId: number) => {
    try {
      await changeMemberRole(targetUserId, 'EDITOR');
      showToast('success', '已變更角色');
    } catch (err) {
      showToast('error', err instanceof MemberApiError ? err.message : '變更角色失敗');
    }
  };

  const handleConfirmRemove = async () => {
    if (!confirmTarget) return;
    try {
      await removeMember(confirmTarget.userId);
      showToast('success', '已移除成員');
    } catch (err) {
      showToast('error', err instanceof MemberApiError ? err.message : '移除成員失敗');
    } finally {
      setConfirmTarget(null);
    }
  };

  const handleConfirmLeave = async () => {
    try {
      await leaveTrip();
      showToast('success', '已退出旅程');
      navigate('/trips');
    } catch (err) {
      showToast('error', err instanceof MemberApiError ? err.message : '退出旅程失敗');
    } finally {
      setConfirmLeave(false);
    }
  };

  const memberRows = members.map((member) => (
    <MemberRow
      key={member.userId}
      member={member}
      myRole={myRole}
      currentUserId={user.userId}
      onChangeRole={() => handleChangeRole(member.userId)}
      onRemove={() => setConfirmTarget({ userId: member.userId, nickname: member.nickname })}
    />
  ));

  return (
    <div className="flex min-h-screen bg-[#f5f8f8]">
      <DesktopSidebar onNavigate={navigate} tripId={id} />

      <div className="flex-1 flex flex-col min-w-0">
        {/* ─── Mobile Header ─── */}
        <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-100">
          <div className="flex items-center justify-between px-5 py-4">
            <div className="flex items-center gap-1">
              <button onClick={() => navigate(-1)} className="flex items-center justify-center w-11 h-11 -ml-2">
                <ChevronLeft size={24} className="text-slate-900" />
              </button>
              <h1 className="text-slate-900 text-[17px] font-bold">成員管理</h1>
            </div>
            <button
              onClick={() => navigate(`/trips/${id}/invite`)}
              className="flex items-center justify-center w-11 h-11"
            >
              <UserPlus size={22} className="text-primary" />
            </button>
          </div>
          <div className="flex items-center justify-between px-5 pb-4">
            <span className="text-sm font-medium text-slate-400">{members.length} 位成員</span>
            <span className="flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-400">
              <Compass size={12} />
              {tripName}
            </span>
          </div>
        </header>

        {/* ─── Desktop Top Bar ─── */}
        <header className="hidden lg:flex items-center justify-between bg-white border-b border-slate-100 px-10 py-5">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 rounded-[10px] border border-slate-200 px-3 py-2 text-[13px] font-medium text-slate-900"
            >
              <ArrowLeft size={16} />
              返回
            </button>
            <h1 className="text-slate-900 text-xl font-bold">成員管理</h1>
            {tripName && (
              <span className="flex items-center gap-1.5 rounded-full bg-slate-100 px-3.5 py-1.5 text-xs font-medium text-slate-500">
                <Compass size={13} />
                {tripName}
              </span>
            )}
          </div>
          <button
            onClick={() => navigate(`/trips/${id}/invite`)}
            className="flex items-center gap-2 h-10 px-5 rounded-full bg-primary text-white text-sm font-semibold"
          >
            <UserPlus size={16} />
            邀請成員
          </button>
        </header>

        {/* ─── Main ─── */}
        <main className="flex-1">
          {/* Mobile list */}
          <div className="lg:hidden">
            {!loading && memberRows}
            {myRole === 'OWNER' && members.length > 0 && (
              <div className="flex items-center justify-center gap-1.5 px-5 py-3 text-xs text-slate-300">
                <ChevronsLeft size={14} />
                向左滑動以管理成員
              </div>
            )}
            {myRole !== 'OWNER' && (
              <button
                onClick={() => setConfirmLeave(true)}
                className="w-full flex items-center justify-center gap-2 px-5 py-4 text-sm font-medium text-red-500"
              >
                <LogOut size={16} />
                退出旅程
              </button>
            )}
          </div>

          {/* Desktop card */}
          <div className="hidden lg:block px-10 py-8">
            <h2 className="text-lg font-bold text-slate-900 mb-4">成員（{members.length} 人）</h2>
            <div className="rounded-2xl bg-white shadow-sm overflow-hidden">{!loading && memberRows}</div>
            {myRole !== 'OWNER' && (
              <button
                onClick={() => setConfirmLeave(true)}
                className="mt-6 flex items-center gap-2 text-sm font-medium text-red-500 hover:text-red-600 transition-colors"
              >
                <LogOut size={16} />
                退出旅程
              </button>
            )}
          </div>
        </main>
      </div>

      <ConfirmDialog
        isOpen={confirmTarget !== null}
        onClose={() => setConfirmTarget(null)}
        onConfirm={handleConfirmRemove}
        title="移除成員"
        message={`確定要將「${confirmTarget?.nickname ?? ''}」移出這趟旅程嗎？`}
        confirmText="移除"
      />

      <ConfirmDialog
        isOpen={confirmLeave}
        onClose={() => setConfirmLeave(false)}
        onConfirm={handleConfirmLeave}
        title="退出旅程"
        message="確定要退出這趟旅程嗎？退出後將無法再存取或編輯此旅程，需重新取得邀請連結才能加入。"
        confirmText="退出"
      />
    </div>
  );
}
